import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { getSelectedAttributeValues } from "components/SharedTable/tableFilterOptionHelpers";
import { styled } from "@mui/material/styles";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

const selectAllOption = {
  type_name: "all",
  name: "Select All",
  type: "categorical"
};

export const CategoryField = ({
  attributeName,
  categoricalValues,
  onSetField,
  filters
}) => {
  const [options, setOptions] = useState([]);
  const [selectedState, setSelectedState] = useState([]);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    let tempOptionList =
      categoricalValues?.map(({ name }) => ({
        type_name: attributeName,
        name,
        type: "categorical"
      })) ?? [];

    setOptions([selectAllOption, ...tempOptionList]);
  }, [attributeName, categoricalValues]);

  const value = useMemo(() => {
    return getSelectedAttributeValues(filters[attributeName], options);
  }, [attributeName, filters, options]);

  const getRenderInput = params => (
    <StyledTextField
      variant="outlined"
      margin="dense"
      {...params}
      InputProps={{ ...params.InputProps }}
      InputLabelProps={{ shrink: false }}
      placeholder={attributeName || null}
    />
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disableClearable
      autoComplete
      autoHighlight
      options={options}
      value={value}
      onInputChange={(_event, newInputValue) => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderInput={getRenderInput}
      placeholder={attributeName || null}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      renderTags={() => []}
      size="small"
      renderOption={(props, option, { selected }) => {
        // eslint-disable-next-line react/prop-types
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions =
          filterOptions(options, {
            inputValue
          }).filter(option => option.name !== "Select All") ?? [];
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? selectedState.length === filteredNonSelectAllOptions.length
            : selected;
        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };
        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event, values) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (selectedState.length === filteredNonSelectAllOptions.length) {
            // If everything was selected, deselect all
            onSetField([], attributeName);
            setSelectedState([]);
          } else {
            // If not everything was selected, select all
            onSetField(filteredNonSelectAllOptions, attributeName);
            setSelectedState(filteredNonSelectAllOptions);
          }
        } else {
          // Normal selection of individual items
          onSetField(values, attributeName);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledTextField = styled(TextField)`
  margin: 0;
`;

CategoryField.propTypes = {
  attributeName: PropTypes.string,
  categoricalValues: PropTypes.array,
  onSetField: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired
};
